import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _groupBy from 'lodash/groupBy';
import _flatMap from 'lodash/flatMap';
import { connect } from 'react-redux';
import { reduxForm, Field, FieldArray, formValueSelector, change, arrayInsert, arrayPush } from 'redux-form';
import { Link } from '@zola/zola-ui';

import { RenderField, RenderSelectField, RenderSubmitButton, RenderCheckboxRadioField, RenderTextareaField, RenderDropdownSelect, RenderHiddenField } from '../../form';
import AddressFields from '../../form/AddressFields';
import GuestAffiliation from '../../../manage/guestlist/GuestAffiliation';
import * as Validations from '../../form/commonValidations';
import { editGuestGroupV2 } from '../../../../actions/GuestActions';
import { hideModal, showSecondaryModal, hideSecondaryModal } from '../../../../actions/ModalActions';
import { Tabs, Pane } from '../../ui/tabs';
import { Prefix, RsvpType } from '../../../manage/guestlist/GuestListTypes';

import './EditGuestGroupModal.less';

function splitLines(text) {
  return (
    <div className="margin-left envelope-recipient">
      {text.split('\n').map((str, key) => (
        <span key={key}>
          {str}
        </span>
      ))}
    </div>
  );
}

function removeFalseyValues(firstName, familyName, relationshipType) {
  if (firstName && familyName) {
    return `${firstName} ${familyName}`;
  } if (firstName) {
    return firstName;
  } if (familyName) {
    return familyName;
  }
  return relationshipType;
}


class RenderRSVP extends Component {
  constructor(props) {
    super(props);

    this.getGuestValue = this.getGuestValue.bind(this);
    this.formatName = this.formatName.bind(this);
    this.isAttending = this.isAttending.bind(this);
  }

  getGuestValue(name, index) {
    const { fields } = this.props;
    const guest = fields.get(index);
    return guest && guest[name];
  }

  formatName(index) {
    const firstName = this.getGuestValue('first_name', index) || null;
    const familyName = this.getGuestValue('family_name', index) || null;
    const relationshipType = this.getGuestValue('relationship_type', index);
    const nameUnknown = this.getGuestValue('name_unknown', index);
    const relationship = (relationshipType === 'CHILD') ? 'Child' : 'Guest';

    return (nameUnknown === true) ? relationship : removeFalseyValues(firstName, familyName, relationship);
  }

  isAttending(index, eventId) {
    const eventInvitations = this.getGuestValue('event_invitations', index);
    const eventInvitation = eventInvitations && eventInvitations[eventId];
    return eventInvitation && eventInvitation.rsvp_type === 'ATTENDING';
  }

  render() {
    const { fields, mealOptions, eventId } = this.props;

    const hasMealOptions = mealOptions && mealOptions.length > 0;

    return (
      <div>
        <div className="row">
          <div className="col-xs-12 col-md-4">&nbsp;</div>
          <div className={`col-xs-12 col-md-4${!hasMealOptions ? ' col-md-offset-4' : ''}`}><div className="form-label">RSVP</div></div>
          {hasMealOptions && (
            <div className="col-xs-12 col-md-4">
              <div className="form-label">Meal Preference</div>
            </div>
          )}
        </div>
        {fields.map((guest, index) => (
          <div key={index} className="row">
            <div className="col-xs-12 col-md-4">
              <p className="form-control-static">{`${index + 1}. ${this.formatName(index)}`}</p>
            </div>
            <div className={`col-xs-12 col-md-4${!hasMealOptions ? ' col-md-offset-4' : ''}`}>
              <Field
                name={`${guest}.event_invitations[${eventId}].rsvp_type`}
                id="edit-guest-group-rsvp"
                options={RsvpType}
                component={RenderSelectField}
                hideEmpty
              />
            </div>
            {hasMealOptions && this.isAttending(index, eventId) && (
              <div className="col-xs-12 col-md-4">
                <Field
                  name={`${guest}.event_invitations[${eventId}].meal_option_id`}
                  id="edit-guest-group-meal-option"
                  options={mealOptions}
                  component={RenderSelectField}
                />
              </div>
            )}
            {hasMealOptions && !this.isAttending(index, eventId) && (
              <div className="col-xs-12 col-md-4">
                <div className="form-group meal-options-disabled">
                  <select className="form-control" disabled>
                    <option value="">Select...</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}

RenderRSVP.propTypes = {
  fields: PropTypes.shape({}),
  mealOptions: PropTypes.arrayOf(PropTypes.shape({})),
  eventId: PropTypes.number,
};

class RenderGuestRow extends Component {
  getGuestValue(name, index) {
    const { guestFormValues } = this.props;
    return guestFormValues && guestFormValues[index] && guestFormValues[index][name];
  }

  getFirstNameRequiredProps(index) {
    return this.getGuestValue('plus_one', index) ? {} : {
      required: true,
      validate: Validations.REQUIRED
    };
  }

  getFamilyNameRequiredProps(index) {
    const isPrimary = this.getGuestValue('relationship_type', index) === 'PRIMARY';
    return isPrimary ? { required: true, validate: Validations.REQUIRED } : {};
  }

  renderPrefixInput(guest, index) {
    if (this.getGuestValue('relationship_type', index) === 'CHILD') {
      return <input className="form-control with-tooltip-override" value="Child" disabled />;
    } if (this.getGuestValue('name_unknown', index)) {
      return <input className="form-control with-tooltip-override" value="Guest" disabled />;
    }
    return (
      <Field
        name={`${guest}.prefix`}
        id="edit-guest-group-prefix"
        options={Prefix}
        component={RenderDropdownSelect}
        hideFormGroup
        tooltip="Addressing your invitations formally? Include titles for your guests."
      />
    );
  }

  renderNameUnknownPlaceholder() {
    return <input className="form-control" type="text" disabled />;
  }

  render() {
    const { name, index, fields, enablePlusOne, onAddGuest } = this.props;
    const guest = name;
    const isPrimary = this.getGuestValue('relationship_type', index) === 'PRIMARY';
    return (
      <div key={index} className="row form-group">
        <div className="col-xs-12 col-md-2">
          <div className="visible-xs visible-sm">
            <div className="form-label">Title <div className="with-tooltip" data-tooltip="Addressing your invitations formally? Include titles for your guests."><i className="zolaicon zolaicon-info" /></div></div>
            {this.renderPrefixInput(guest, index)}
          </div>
          <div className="hidden-xs hidden-sm">
            {this.renderPrefixInput(guest, index)}
          </div>
        </div>
        <div className="col-xs-12 col-md-4">
          <div className="visible-xs visible-sm">
            <div className="form-label">First Name <span className="text-danger danger-asterisk">*</span></div>
          </div>
          {this.getGuestValue('name_unknown', index) && this.renderNameUnknownPlaceholder()}
          {!this.getGuestValue('name_unknown', index) && (
            <Field
              name={`${guest}.first_name`}
              id="add-guest-group-first-name"
              type="text"
              component={RenderField}
              required
              validate={Validations.REQUIRED}
              disabled={this.getGuestValue('name_unknown', index)}
              hideValue={this.getGuestValue('name_unknown', index)}
              hideFormGroup
            />
          )}
        </div>
        <div className="col-xs-12 col-md-4">
          <div className="visible-xs visible-sm">
            <div className="form-label">Last Name {isPrimary && <span className="text-danger danger-asterisk">*</span>}</div>
          </div>
          {this.getGuestValue('name_unknown', index) && this.renderNameUnknownPlaceholder()}
          {!this.getGuestValue('name_unknown', index) && (
            <Field
              name={`${guest}.family_name`}
              id="add-guest-group-family-name"
              type="text"
              component={RenderField}
              {...this.getFamilyNameRequiredProps(index)}
              disabled={this.getGuestValue('name_unknown', index)}
              hideFormGroup
            />
          )}
        </div>
        <div className="col-xs-12 col-md-2 visible-xs visible-sm">
          <div className="form-label">Suffix</div>
          {index > 0 && (
            <div className="guest-delete" onClick={() => fields.remove(index)}>X</div>
          )}
          {this.getGuestValue('name_unknown', index) && this.renderNameUnknownPlaceholder()}
          {!this.getGuestValue('name_unknown', index) && (
            <Field
              name={`${guest}.suffix`}
              id="add-guest-group-suffix"
              type="text"
              component={RenderField}
              disabled={this.getGuestValue('name_unknown', index)}
              hideFormGroup
            />
          )}
        </div>
        <div className="col-xs-12 col-md-2 visible-md visible-lg">
          {index > 0 && (
            <div className="guest-delete" onClick={() => fields.remove(index)}>
              <div className="with-tooltip" data-tooltip="Delete guest">X</div>
            </div>
          )}
          {this.getGuestValue('name_unknown', index) && this.renderNameUnknownPlaceholder()}
          {!this.getGuestValue('name_unknown', index) && (
            <Field
              name={`${guest}.suffix`}
              id="add-guest-group-suffix"
              type="text"
              component={RenderField}
              disabled={this.getGuestValue('name_unknown', index)}
              hideFormGroup
            />
          )}
        </div>
        {enablePlusOne && index === 0 && (
          <div className="col-xs-12 margin-top">
            <div className="with-tooltip" data-tooltip="Control who brings a plus-one.">
              <Link role="button" className="plus-one-btn" onClick={() => onAddGuest('PARTNER')}>
                + Add Plus One
              </Link>
              <i className="zolaicon zolaicon-info visible-xs-inline visible-sm-inline margin-left-2x" />
            </div>
          </div>
        )}
        {index > 0 && (
          <div className="col-xs-12">
            <div className="with-tooltip" data-tooltip="Check off if you don't know a guest's or plus-one's name.">
              <Field
                id={`${guest}-add-guest-group-name-unknown`}
                name={`${guest}.name_unknown`}
                component={RenderCheckboxRadioField}
                type="checkbox"
                label="Name Unknown"
                styleOverride
                className="col-xs-10 col-md-12"
              />
              <span className="zolaicon zolaicon-info guest-unknown-zolaicon visible-xs visible-sm col-xs-2" />
            </div>
          </div>
        )}
        {(index === fields.length - 1) && (
          <div className="col-xs-12">
            <Link role="button" onClick={() => onAddGuest('CHILD')}>+ Add Child</Link>
          </div>
        )}
      </div>
    );
  }
}

RenderGuestRow.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  fields: PropTypes.shape({}).isRequired,
  enablePlusOne: PropTypes.bool.isRequired,
  guestFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  onAddGuest: PropTypes.func,
};

const RenderGuests = (props) => {
  const { fields, guestFormValues, onAddGuest } = props;
  const guestsByRelationshipType = _groupBy(fields.getAll(), guest => guest.relationship_type);
  const enablePlusOne = !guestsByRelationshipType.PARTNER;

  return (
    <div>
      <div className="row hidden-xs hidden-sm">
        <div className="col-md-2">
          <div className="form-label">Title</div>
        </div>
        <div className="col-md-4">
          <div className="form-label">First Name</div>
        </div>
        <div className="col-md-4">
          <div className="form-label">Last Name</div>
        </div>
        <div className="col-md-2">
          <div className="form-label">Suffix</div>
        </div>
      </div>
      {fields.map((guestNameField, guestIndex, guestFieldsRef) => (
        <RenderGuestRow
          key={guestIndex}
          name={guestNameField}
          index={guestIndex}
          fields={guestFieldsRef}
          enablePlusOne={enablePlusOne}
          guestFormValues={guestFormValues}
          onAddGuest={onAddGuest}
        />
      ))}
    </div>
  );
};

RenderGuests.propTypes = {
  fields: PropTypes.shape({}),
  guestFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  onAddGuest: PropTypes.func,
};

const RenderRsvpQuestion = ({ question }) => (
  <li className="rsvp-question">
    <Field
      id={`edit-guest-group-${question.id}-id`}
      name={`rsvp_question_answers[${question.id}].rsvp_question_id`}
      type="hidden"
      component={RenderHiddenField}
    />
    <Field
      id={`edit-guest-group-${question.id}-answer`}
      name={`rsvp_question_answers[${question.id}].answer`}
      type="text"
      component={RenderField}
      label={question.question}
      className="rsvp-question-field"
    />
  </li>
);

RenderRsvpQuestion.propTypes = {
  question: PropTypes.shape({}),
};

function RenderEventInvitation({ eventInvitation, eventFormValues, index, handleEventInvitation, disabled }) {
  const event = eventFormValues[index];
  let trimmedEventName = '';
  if (eventFormValues && eventFormValues[index].name) {
    const { name } = eventFormValues[index];
    trimmedEventName = name.length > 30 ? `${name.slice(0, 30)}...` : name;
  }

  return (
    <Field
      id={eventInvitation}
      name={`${eventInvitation}.checked`}
      component={RenderCheckboxRadioField}
      label={trimmedEventName}
      type="checkbox"
      styleOverride
      inline
      checkedOverride
      checked={event.checked === true}
      onChangeAction={() => handleEventInvitation(event, index)}
      disableDefaultAction
      className={disabled ? 'checkbox-disabled' : 'checkbox-secondary'}
      disabled={disabled}
    />
  );
}

RenderEventInvitation.propTypes = {
  eventInvitation: PropTypes.string,
  index: PropTypes.number,
  eventFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  handleEventInvitation: PropTypes.func,
  disabled: PropTypes.bool,
};

function RenderEventInvitations({ fields, id, label, hideFormGroup, meta: { touched, error }, eventFormValues, handleEventInvitation, disabled }) {
  return (
    <div className={`${hideFormGroup ? '' : 'form-group'} ${disabled ? 'event-invitations-disabled' : ''} ${touched && error ? 'has-error' : ''}`}>
      <label htmlFor={id} className="margin-right-2x">{label}</label>
      <div id={id} className="visible-xs-inline visible-sm-inline visible-md-inline visible-lg-inline">
        {fields.map((eventInvitation, index, fieldsRef) => (
          <RenderEventInvitation
            key={index}
            eventInvitation={eventInvitation}
            index={index}
            fields={fieldsRef}
            eventFormValues={eventFormValues}
            handleEventInvitation={handleEventInvitation}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

RenderEventInvitations.propTypes = {
  fields: PropTypes.shape({}),
  id: PropTypes.string,
  label: PropTypes.string,
  hideFormGroup: PropTypes.bool,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string
  }),
  eventFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  handleEventInvitation: PropTypes.func,
  disabled: PropTypes.bool,
};

const paneKeyToIndex = {
  GUESTS: 0,
  CONTACT: 1,
  ENVELOPE: 1,
  RSVP: 2
};

class EditGuestGroupForm extends Component {
  constructor(props) {
    super(props);

    this.onToggleEdit = this.onToggleEdit.bind(this);

    this.state = {
      editEnvelopeRecipient: false
    };
  }

  onToggleEdit() {
    const { editEnvelopeRecipient } = this.state;
    this.setState({
      editEnvelopeRecipient: !editEnvelopeRecipient
    });
  }

  render() {
    const {
      wedding,
      handleSubmit,
      pristine,
      initialValues,
      submitting,
      valid,
      initialPanel,
      guestFormValues,
      eventFormValues,
      onAddGuest,
    } = this.props;
    const { editEnvelopeRecipient } = this.state;

    return (
      <form onSubmit={handleSubmit} className="edit-guest-group-form">
        <Tabs selected={paneKeyToIndex[initialPanel] || 0}>
          <Pane label="Guest Info">
            <div>
              <FieldArray
                name="guests"
                component={RenderGuests}
                guestFormValues={guestFormValues}
                onAddGuest={type => onAddGuest(type, eventFormValues)}
              />
              <div className="row">
                <div className="col-xs-12 col-md-6">
                  <div className="visible-xs visible-sm">
                    <Field
                      name="affiliation"
                      id="add-guest-group-guest-affiliation-test"
                      component={RenderDropdownSelect}
                      options={GuestAffiliation(wedding.owner_first_name, wedding.partner_first_name)}
                      label="Relationship To You"
                      tooltip="Indicate how you know your guests to help filter your list."
                      hideEmpty
                    />
                  </div>
                  <div className="visible-md visible-lg">
                    <Field
                      name="affiliation"
                      id="add-guest-group-guest-affiliation-test"
                      component={RenderDropdownSelect}
                      options={GuestAffiliation(wedding.owner_first_name, wedding.partner_first_name)}
                      label="Relationship To You"
                      tooltip="Indicate how you know your guests to help filter your list."
                      hideEmpty
                    />
                  </div>
                </div>
              </div>
            </div>
          </Pane>
          <Pane label="Contact Info & Invitations">
            <div>
              {editEnvelopeRecipient && (
                <div className="visible-xs visible-sm">
                  <Field
                    name="envelope_recipient_override"
                    label="Name As Appears On The Outer Envelope"
                    id="edit-guest-group-envelope_recipient_override"
                    type="text"
                    placeholder={initialValues.envelope_recipient}
                    component={RenderTextareaField}
                    styleOverride
                    tooltip="We formatted your guests' info for your outer envelopes, but feel free to edit how you'd like any of these to appear, including editing titles."
                  />
                </div>
              )}
              {editEnvelopeRecipient && (
                <div className="with-tooltip no-tooltip-on-mobile edit-guest-name visible-md visible-lg" data-tooltip="We formatted your guests' info for your outer envelopes, but feel free to edit how you'd like any of these to appear, including editing titles.">
                  <Field
                    name="envelope_recipient_override"
                    label="Name As Appears On The Outer Envelope"
                    id="edit-guest-group-envelope_recipient_override"
                    type="text"
                    placeholder={initialValues.envelope_recipient}
                    component={RenderTextareaField}
                    styleOverride
                  />
                </div>
              )}
              {!editEnvelopeRecipient && (
                <div className="envelope-recipient-container visible-md visible-lg">
                  <div className="form-label">
                    <div className="visible-xs-inline visible-sm-inline">Name as appears on the outer envelope</div>
                    <div className="with-tooltip" data-tooltip="We formatted your guests' info for your outer envelopes, but feel free to edit how you'd like any of these to appear, including editing titles.">
                      <i className="zolaicon zolaicon-info visible-xs visible-sm" />
                      <span className="hidden-xs hidden-sm form-label">Name as appears on the outer envelope</span>
                    </div>
                  </div>
                  <div className="envelope margin-top padding-bottom-2x">
                    {splitLines(initialValues.envelope_recipient)}
                    {/* <i onClick={this.onToggleEdit} className="glyphicon glyphicon-pencil" /> */}
                    <i onClick={this.onToggleEdit} className="zolaicon zolaicon-edit" />
                  </div>
                </div>
              )}
            </div>
            <div className="mailing-address">
              <AddressFields selector="editGuestGroupForm" minimal optional addressMessage="Formally addressing your invitations? Avoid Rd, St, Apt, etc." />
            </div>
            <div className="row margin-bottom">
              <div className="col-xs-12 col-md-6">
                <Field
                  name="email_address"
                  label="Email"
                  id="edit-guest-group-email"
                  type="email"
                  component={RenderField}
                />
              </div>
              <div className="col-xs-12 col-md-6">
                <Field
                  name="mobile_phone"
                  label="Mobile"
                  id="edit-guest-group-mobile-phone"
                  type="tel"
                  component={RenderField}
                />
              </div>
            </div>
            <div className="form-group">
              <span className="form-label margin-right-2x" style={{ minWidth: '200px' }}>Sent save the date?</span>
              <Field
                id="edit-guest-group-save-the-date-sent-yes"
                name="save_the_date_sent"
                component={RenderCheckboxRadioField}
                type="radio"
                label="Yes"
                styleOverride
                value="true"
                inline
              />
              <Field
                id="edit-guest-group-save-the-date-sent-no"
                name="save_the_date_sent"
                component={RenderCheckboxRadioField}
                type="radio"
                label="No"
                styleOverride
                value="false"
                inline
              />
            </div>
            <div className="form-group">
              <span className="form-label margin-right-2x" style={{ minWidth: '200px' }}>Sent wedding invitation?</span>
              <Field
                id="edit-guest-group-invitation-sent-yes"
                name="invitation_sent"
                component={RenderCheckboxRadioField}
                type="radio"
                label="Yes"
                styleOverride
                value="true"
                inline
              />
              <Field
                id="edit-guest-group-invitation-sent-no"
                name="invitation_sent"
                component={RenderCheckboxRadioField}
                type="radio"
                label="No"
                styleOverride
                value="false"
                inline
              />
            </div>
          </Pane>
        </Tabs>
        <div className="edit-guest-group-submit-container">
          <div className="pull-right edit-guest-group-submit">
            <RenderSubmitButton className="edit-guest-submit" pristine={pristine} submitting={submitting} valid={valid} />
          </div>
        </div>
      </form>
    );
  }
}

EditGuestGroupForm.propTypes = {
  wedding: PropTypes.shape({
    owner_first_name: PropTypes.string.isRequired,
    partner_first_name: PropTypes.string.isRequired
  }),
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  initialPanel: PropTypes.string,
  initialValues: PropTypes.shape({}),
  guestFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  eventFormValues: PropTypes.arrayOf(PropTypes.shape({})),
  onAddGuest: PropTypes.func,
};

const selector = formValueSelector('editGuestGroupForm');

const mapStateToProps = (state, ownProps) => {
  let initialValues = {};
  const guestGroupsById = state.guestList.guests.byId;
  const guestGroup = guestGroupsById[ownProps.guestGroupId] || {};

  if (guestGroup.guests && guestGroup.guests.length > 0) {
    const guests = guestGroup.guests.map(guest => ({
      id: guest.id,
      prefix: guest.prefix,
      first_name: guest.first_name,
      middle_name: guest.middle_name,
      family_name: guest.family_name,
      suffix: guest.suffix,
      rsvp: guest.rsvp,
      meal_option_id: guest.meal_option && guest.meal_option.id,
      relationship_type: guest.relationship_type,
      name_unknown: !guest.first_name && !guest.family_name,
      source: guest.source,
      event_invitations: guest.event_invitations.reduce((result, eventInvitation) => {
        result.orderedIds.push(eventInvitation.event_id);
        result[eventInvitation.event_id] = { ...eventInvitation, checked: true }; // eslint-disable-line
        return result;
      }, { orderedIds: [] })
    }));

    const eventInvitations = guests[0].event_invitations.orderedIds.reduce((result, eventId) => {
      result.orderedIds.push(eventId);
      result[eventId] = { // eslint-disable-line
        event_id: eventId,
        checked: true,
      };
      return result;
    }, { orderedIds: [] });

    const rsvpQuestionAnswers = guestGroup.rsvp_question_answers.reduce((result, answer) => {
      result.orderedIds.push(answer.rsvp_question_id);
      result[answer.rsvp_question_id] = answer; // eslint-disable-line
      return result;
    }, { orderedIds: [] });

    initialValues = {
      id: guestGroup.id,
      wedding_account_id: guestGroup.wedding_account_id,
      address_1: guestGroup.address_1,
      address_2: guestGroup.address_2,
      city: guestGroup.city,
      state_province: guestGroup.state_province,
      postal_code: guestGroup.postal_code,
      country_code: guestGroup.country_code || 'US',
      email_address: guestGroup.email_address,
      home_phone: guestGroup.home_phone,
      mobile_phone: guestGroup.mobile_phone,
      affiliation: guestGroup.affiliation,
      tier: guestGroup.tier,
      invitation_sent: guestGroup.invitation_sent.toString(),
      invited: guestGroup.invited.toString(),
      save_the_date_sent: guestGroup.save_the_date_sent.toString(),
      envelope_recipient: guestGroup.envelope_recipient,
      envelope_recipient_override: guestGroup.envelope_recipient_override,
      rsvp_question_answers: rsvpQuestionAnswers,
      guests,
      event_invitations: eventInvitations.orderedIds.map(id => eventInvitations[id])
    };
  }

  return {
    initialValues,
    invitedFormValue: selector(state, 'invited'),
    guestFormValues: selector(state, 'guests'),
    eventFormValues: selector(state, 'event_invitations') || [],
    initialPanel: ownProps.activeTab,
    wedding: state.wedding.wedding,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onSubmit: (createGuestGroupRequest) => {
    dispatch(editGuestGroupV2(createGuestGroupRequest))
      .then(json => { if (ownProps.callback) ownProps.callback(json); })
      .then(() => dispatch(hideModal()));
  },
  handleEventInvitation: (guests, eventInvitation, eventIndex) => {
    const isChecked = eventInvitation.checked === true;
    const eventInvitations = _flatMap(guests, guest => guest.event_invitations[eventInvitation.event_id]);
    const hasResponses = eventInvitations.some(eventInvite => eventInvite.event_id === eventInvitation.event_id && eventInvite.rsvp_at !== null);
    if (isChecked && hasResponses) {
      const onConfirmAction = () => {
        dispatch(change('editGuestGroupForm', `event_invitations[${eventIndex}].checked`, false));
        guests.forEach((guest, index) => {
          if (guest.event_invitations) {
            dispatch(change('editGuestGroupForm', `guests[${index}].event_invitations[${eventInvitation.event_id}].checked`, false));
          }
        });
        return dispatch(hideSecondaryModal());
      };
      dispatch(showSecondaryModal('BULK_UNINVITE_CONFIRMATION', { onConfirmAction }, { size: 'md' }));
    } else {
      dispatch(change('editGuestGroupForm', `event_invitations[${eventIndex}].checked`, !isChecked));
      guests.forEach((guest, index) => {
        if (guest.event_invitations) {
          dispatch(change('editGuestGroupForm', `guests[${index}].event_invitations[${eventInvitation.event_id}].checked`, !isChecked));
        }
      });
    }
  },
  onUninvite: (guests) => {
    const eventInvitations = _flatMap(guests, (guest) => { // eslint-disable-line
      return guest.event_invitations.orderedIds.map(eventId => guest.event_invitations[eventId]);
    });
    const hasResponses = eventInvitations.some(eventInvitation => eventInvitation.rsvp_at !== null);
    if (hasResponses) {
      const onConfirmAction = () => {
        dispatch(change('editGuestGroupForm', 'invited', 'false'));
        dispatch(hideSecondaryModal());
      };
      dispatch(showSecondaryModal('UNINVITE_GUEST_CONFIRMATION', { onConfirmAction }, { size: 'md' }));
    } else {
      dispatch(change('editGuestGroupForm', 'invited', 'false'));
    }
  },
  onAddGuest: (type, eventFormValues) => {
    const eventInvitations = eventFormValues.reduce((result, eventInvitation) => {
      result.orderedIds.push(eventInvitation.event_id);
      result[eventInvitation.event_id] = { // eslint-disable-line
        event_id: eventInvitation.event_id,
        checked: eventInvitation.checked,
        meal_option_id: null,
        rsvp_at: null,
        rsvp_type: 'NO_RESPONSE',
      };
      return result;
    }, { orderedIds: [] });
    if (type === 'PARTNER') {
      dispatch(arrayInsert('editGuestGroupForm', 'guests', 1, { relationship_type: 'PARTNER', source: 'MANUAL', event_invitations: eventInvitations }));
    } else if (type === 'CHILD') {
      dispatch(arrayPush('editGuestGroupForm', 'guests', { relationship_type: 'CHILD', source: 'MANUAL', event_invitations: eventInvitations }));
    }
  }
});

const EditGuestGroupFormContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({
  form: 'editGuestGroupForm',
  destroyOnUnmount: true, // <------ preserve form data
  forceUnregisterOnUnmount: false,
})(EditGuestGroupForm));

const EditGuestGroupModal = ({ guestGroupId, activeTab, callback }) => (
  <div id="edit-guest-group-modal">
    <div className="modal-header">
      <div className="row">
        <div className="col-xs-6 col-sm-6 col-md-6">
          <h3 className="modal-title margin-bottom">Edit Guest</h3>
        </div>
      </div>
    </div>
    <div className="modal-body">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xs-12 col-md-12">
            <EditGuestGroupFormContainer guestGroupId={guestGroupId} activeTab={activeTab} callback={callback} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

EditGuestGroupModal.propTypes = {
  guestGroupId: PropTypes.number.isRequired,
  activeTab: PropTypes.oneOf([
    'GUESTS', 'CONTACT', 'ENVELOPE', 'RSVP'
  ]),
  callback: PropTypes.func,
};

export default EditGuestGroupModal;



// WEBPACK FOOTER //
// ./src/components/common/modals/EditGuestGroupModal/EditGuestGroupModal.jsx